import type { IntrinsicSystemProps } from '@hover/blueprint';
import { Box, forwardRef, motion, system, useSize } from '@hover/blueprint';
import { useMotionValue, useMotionValueEvent, useScroll } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { hideScrollbars } from 'src/shared/utils/styles';

const MotionBoxHideScroll = motion(hideScrollbars(Box));

type DivProps = IntrinsicSystemProps<'div'>;
export interface CustomHorizontalScrollProps extends DivProps {
  thumbSize?: DivProps['height'];
  scrollBarDisplay?: DivProps['display'];
  scrollBarHorizontalPadding?: DivProps['padding'];
}

const CustomHorizontalScroll = forwardRef<CustomHorizontalScrollProps, 'div'>(
  (
    {
      children,
      thumbSize = '5px',
      scrollBarDisplay = { base: 'none', tablet: 'block' },
      scrollBarHorizontalPadding = 0,
      ...props
    },
    ref,
  ) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);

    // We need to re-render to update the thumb width when either the size of
    // the scroll track or the size of the scrolling content change. The
    // `useSize` hook wires up a `ResizeObserver` to state, ensuring this
    // component re-renders when the size of either element changes.
    const contentSize = useSize(contentRef);
    const trackSize = useSize(trackRef);

    const motionValue = useMotionValue(0);

    const [thumbWidth, setThumbWidth] = useState(0);

    const { scrollX } = useScroll({ container: containerRef });

    const getScrollDistance = useCallback(() => {
      if (!containerRef.current || !trackRef.current)
        return { scrollDistance: 0, trackDistance: 0 };

      const { scrollWidth, clientWidth } = containerRef.current;
      const { clientWidth: trackWidth } = trackRef.current;

      return {
        scrollDistance: scrollWidth - clientWidth,
        trackDistance: trackWidth - thumbWidth,
      };
    }, [thumbWidth]);

    /**
     * We need to keep `aria-valuenow` up to date with the scroll position for
     * accessibility, but we don't want to trigger renders while we scroll
     *
     * @param value the current scroll position as a decimal percentage
     */
    const setAriaValueNow = useCallback((value: number) => {
      if (!trackRef.current) return;

      trackRef.current.setAttribute(
        'aria-valuenow',
        Math.round(value * 100).toString(),
      );
    }, []);

    useMotionValueEvent(scrollX, 'change', x => {
      const { scrollDistance, trackDistance } = getScrollDistance();
      const percent = x / scrollDistance;

      setAriaValueNow(percent);

      motionValue.set(Math.max(percent * trackDistance, 0));
    });

    useMotionValueEvent(motionValue, 'change', x => {
      if (!containerRef.current) return;

      const { scrollDistance, trackDistance } = getScrollDistance();
      const percent = x / trackDistance;

      setAriaValueNow(percent);

      containerRef.current.scrollLeft = percent * scrollDistance;
    });

    useEffect(() => {
      if (!containerRef.current || !trackRef.current) return;

      const { scrollDistance } = getScrollDistance();

      const { scrollWidth, clientWidth, scrollLeft } = containerRef.current;
      const { clientWidth: trackWidth } = trackRef.current;

      setAriaValueNow(scrollLeft / scrollDistance);
      setThumbWidth((clientWidth / scrollWidth) * trackWidth);
    }, [
      getScrollDistance,
      setAriaValueNow,
      // Although we don't access these values directly, we need them as
      // dependencies to ensure the thumb size is updated correctly when the
      // content or track size changes
      trackSize?.width,
      contentSize?.width,
    ]);

    return (
      <system.div
        overflow="hidden"
        position="relative"
        width="100%"
        {...props}
        ref={ref}
      >
        <MotionBoxHideScroll
          overflowX="auto"
          position="relative"
          ref={containerRef}
        >
          <system.div ref={contentRef} width="100%">
            {children}
          </system.div>
        </MotionBoxHideScroll>
        <system.div
          aria-valuemax={100}
          aria-valuemin={0}
          backgroundColor="neutral.100"
          borderRadius="round"
          bottom={0}
          display={scrollBarDisplay}
          height={thumbSize}
          left={scrollBarHorizontalPadding}
          overflow="hidden"
          position="absolute"
          ref={trackRef}
          right={scrollBarHorizontalPadding}
          role="scrollbar"
        >
          <motion.div
            backgroundColor="neutral.700"
            borderRadius="round"
            cursor="pointer"
            drag="x"
            dragConstraints={trackRef}
            dragElastic={0}
            dragMomentum={false}
            height="100%"
            style={{ width: `${thumbWidth}px`, x: motionValue }}
          />
        </system.div>
      </system.div>
    );
  },
);

export { CustomHorizontalScroll };
